// ============================================================================
// FarmerTracksTab — producer track plans (Build S5 Task #22; 4×4 grid S5p Task #34).
// ============================================================================
// Reached at #my/tracks. Self-chrome (back link to #my). Renders, per ENROLLMENT
// (farm × program-year), a
// 4×4 milestone grid: track COLUMNS (Food Safety · Conservation · Production ·
// Sales & Marketing) × stage ROWS (Develop · Implement · Complete · Continuous
// Improvement). Each cell shows the milestone's status; actively-worked cells
// (in_progress/submitted/under_review) also show a "Missing: <codes>" hint — the
// evidence_required doc-type codes not yet satisfied, from the API's
// missing_evidence field. Dormant (not-yet-started) tracks are dimmed. Draft def
// cells are hidden by the server. READ-ONLY.
// ============================================================================

import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Loader2, AlertCircle, ArrowLeft, LayoutGrid, CheckCircle2, XCircle, MinusCircle, Clock,
} from 'lucide-react';
import {
  api, type TrackPlanSummary, type FarmerTrackDetail, type FarmMilestone,
  type TrackCode, type StageCode, type PracticeInstance,
} from '../api';
import { useFarmerAuth } from '../farmerAuth';

// Column/row order is producer-facing (§5.3): tracks across the top, stages down.
const TRACK_COLUMNS: { code: TrackCode; label: string }[] = [
  { code: 'FSAF', label: 'Food Safety' },
  { code: 'CONS', label: 'Conservation' },
  { code: 'PROD', label: 'Production' },
  { code: 'MKTG', label: 'Sales & Marketing' },
];
const STAGE_ROWS: { code: StageCode; label: string }[] = [
  { code: 'DEVELOP', label: 'Develop' },
  { code: 'IMPLEMENT', label: 'Implement' },
  { code: 'COMPLETE', label: 'Complete' },
  { code: 'CONTINUOUS_IMPROVEMENT', label: 'Continuous Improvement' },
];

// Show the missing-evidence hint only where it's actionable — while a milestone
// is being worked. not_started/terminal cells stay quiet (no wall of "Missing"
// on a brand-new producer's grid).
const ACTIVE_WORK = new Set(['in_progress', 'submitted', 'under_review']);

function statusIcon(status: string) {
  if (status === 'completed') return <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />;
  if (status === 'not_started') return <MinusCircle className="w-4 h-4 text-gray-300 shrink-0" />;
  if (status === 'not_applicable' || status === 'waived') return <MinusCircle className="w-4 h-4 text-gray-400 shrink-0" />;
  return <Clock className="w-4 h-4 text-amber-600 shrink-0" />;
}

function GridCell({ milestone, dormant, practices }: { milestone: FarmMilestone | undefined; dormant: boolean; practices?: PracticeInstance[] }) {
  const hasPractices = !!practices && practices.length > 0;
  if (!milestone && !hasPractices) {
    return <div className="border border-gray-100 bg-gray-50 p-2 text-xs text-gray-300 flex items-center justify-center">—</div>;
  }
  const showMissing = !!milestone && ACTIVE_WORK.has(milestone.status) && milestone.missing_evidence.length > 0;
  return (
    <div className={`border border-gray-100 p-2 text-xs space-y-1 ${dormant ? 'bg-gray-50/70' : 'bg-white'}`}>
      {milestone ? (
        <div className="flex items-center gap-1">
          {statusIcon(milestone.status)}
          <span className={dormant ? 'text-gray-400' : 'text-gray-700'}>{milestone.status.replace(/_/g, ' ')}</span>
        </div>
      ) : (
        <div className="text-gray-300">—</div>
      )}
      {showMissing && (
        <div className="flex items-start gap-1 text-amber-700 leading-tight">
          <XCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>Missing: {milestone!.missing_evidence.join(', ')}</span>
        </div>
      )}
      {hasPractices && (
        <div className="pt-1 border-t border-gray-100 space-y-0.5">
          <div className="text-[10px] uppercase tracking-wide text-gray-400">Practices</div>
          {practices!.map((p) => (
            <div key={p.practice_instance_id} className="flex items-center justify-between gap-1 text-gray-600">
              <span className="truncate" title={p.practice_name ?? undefined}>
                {p.nrcs_code ?? '—'}{p.practice_name ? ` · ${p.practice_name}` : ''}
              </span>
              <span className="text-[10px] text-gray-400 shrink-0">{p.status.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FarmGrid({ farmName, plansByTrack, detailByPlan }: {
  farmName: string;
  plansByTrack: Partial<Record<TrackCode, TrackPlanSummary>>;
  detailByPlan: Record<string, FarmerTrackDetail>;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
      <div className="text-sm font-semibold text-gray-800">{farmName}</div>
      <div className="overflow-x-auto">
        <div
          className="grid min-w-[44rem] gap-px bg-gray-100"
          style={{ gridTemplateColumns: 'minmax(6.5rem, auto) repeat(4, minmax(9rem, 1fr))' }}
        >
          {/* header row: corner + track columns */}
          <div className="bg-white" />
          {TRACK_COLUMNS.map((col) => {
            const plan = plansByTrack[col.code];
            const dormant = !plan || plan.status === 'available';
            return (
              <div key={col.code} className={`bg-white p-2 text-center text-sm font-semibold ${dormant ? 'text-gray-400' : 'text-uif-green-dark'}`}>
                {col.label}
                <span className={`block text-[10px] font-normal ${dormant ? 'text-gray-400' : 'text-green-700'}`}>
                  {!plan ? 'not enrolled' : plan.status === 'available' ? 'not started' : plan.status.replace(/_/g, ' ')}
                </span>
              </div>
            );
          })}
          {/* stage rows */}
          {STAGE_ROWS.map((row) => (
            <Fragment key={row.code}>
              <div className="bg-white p-2 text-xs font-medium text-gray-600 flex items-center">{row.label}</div>
              {TRACK_COLUMNS.map((col) => {
                const plan = plansByTrack[col.code];
                const detail = plan ? detailByPlan[plan.track_plan_id] : undefined;
                const milestone = detail?.milestones.find((m) => m.stage_code === row.code);
                const dormant = !plan || plan.status === 'available';
                // S6: practice instances surface in the IMPLEMENT cell of their
                // track (they ARE the implementation work). detail.practices is
                // already scoped to this plan's enrollment_id + track_code by the API.
                const practices = row.code === 'IMPLEMENT' ? detail?.practices : undefined;
                return <GridCell key={col.code} milestone={milestone} dormant={dormant} practices={practices} />;
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

export function FarmerTracksTab() {
  const { token: authToken } = useFarmerAuth();
  const token = authToken ?? '';
  const [plans, setPlans] = useState<TrackPlanSummary[] | null>(null);
  const [detailByPlan, setDetailByPlan] = useState<Record<string, FarmerTrackDetail>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Signed-out direct nav to #my/tracks: redirect rather than fire a doomed
    // 401'd /api/farmer/tracks call (mirrors FarmerHomePage's token guard).
    if (!token) { window.location.hash = '#farmer-signin'; return; }
    let live = true;
    (async () => {
      try {
        setError(null);
        const res = await api.farmerTracks(token);
        // Populate grid cells from each track's detail (status + missing_evidence),
        // in bounded-concurrency batches so a producer with many enrollments doesn't
        // burst N detail requests at once. A single failed detail leaves that
        // track's column blank rather than failing the whole grid.
        const byPlan: Record<string, FarmerTrackDetail> = {};
        const CONCURRENCY = 4;
        for (let i = 0; i < res.track_plans.length; i += CONCURRENCY) {
          await Promise.all(res.track_plans.slice(i, i + CONCURRENCY).map(async (t) => {
            try { byPlan[t.track_plan_id] = await api.farmerTrackDetail(token, t.track_plan_id); }
            catch { /* leave this track's column blank */ }
          }));
        }
        if (!live) return;
        setDetailByPlan(byPlan);
        setPlans(res.track_plans);
      } catch (err: any) {
        if (live) setError(err?.message ?? 'Could not load your track plans');
      }
    })();
    return () => { live = false; };
  }, [token]);

  // One 4×4 grid per ENROLLMENT (farm × program-year). A farm can hold more than
  // one enrollment (one per program_year) and the list returns all of them; the
  // DB guarantees track_code is unique WITHIN an enrollment (unique index on
  // (enrollment_id, track_code)), so keying by enrollment_id can never silently
  // drop a plan the way keying by farm_code would.
  const grids = useMemo(() => {
    if (!plans) return [];
    const byEnrollment = new Map<string, { enrollment_id: string; farm_name: string; plansByTrack: Partial<Record<TrackCode, TrackPlanSummary>> }>();
    for (const p of plans) {
      let entry = byEnrollment.get(p.enrollment_id);
      if (!entry) { entry = { enrollment_id: p.enrollment_id, farm_name: p.farm_name, plansByTrack: {} }; byEnrollment.set(p.enrollment_id, entry); }
      entry.plansByTrack[p.track_code] = p;
    }
    return Array.from(byEnrollment.values());
  }, [plans]);

  return (
    <div className="max-w-4xl mx-auto space-y-4 px-4 py-6">
      <a href="#my" className="inline-flex items-center gap-1 text-sm text-uif-green-dark hover:underline">
        <ArrowLeft className="h-4 w-4" /> My account
      </a>

      <div className="flex items-center gap-2">
        <LayoutGrid className="w-6 h-6 text-uif-green-dark" />
        <h1 className="text-xl font-bold text-gray-900">Track plans</h1>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {!error && (
        !plans ? (
          <div className="flex items-center gap-2 text-gray-500 p-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        ) : grids.length === 0 ? (
          <p className="text-sm text-gray-500 bg-white rounded-lg border border-gray-200 p-6">No track plans yet.</p>
        ) : (
          <div className="space-y-4">
            {grids.map((g) => (
              <FarmGrid key={g.enrollment_id} farmName={g.farm_name} plansByTrack={g.plansByTrack} detailByPlan={detailByPlan} />
            ))}
          </div>
        )
      )}
    </div>
  );
}
